if (!customElements.get('bm-landing-ring-guide')) {
  class BmLandingRingGuide extends HTMLElement {
    connectedCallback() {
      this.navItems = Array.from(this.querySelectorAll('[data-bm-lrg-nav-item]'));
      this.panels = Array.from(this.querySelectorAll('[data-bm-lrg-panel]'));

      if (this.navItems.length === 0) return;

      this.activeIndex = 0;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.navItems.forEach((item, index) => {
        const trigger = item.querySelector('[data-bm-lrg-nav-trigger]');
        if (!trigger) return;
        trigger.addEventListener('click', () => this.goTo(index));
        trigger.addEventListener('keydown', (event) => this.onTriggerKeydown(event, index));
      });

      this.initAccordion();
    }

    // Mobile-only single-open accordion (≤989px). Opening one item closes the
    // rest; clicking the open item collapses it. Native <button> handles keys.
    initAccordion() {
      this.accItems = Array.from(this.querySelectorAll('[data-bm-lrg-acc-item]'));
      if (this.accItems.length === 0) return;

      this.accItems.forEach((item, index) => {
        const trigger = item.querySelector('[data-bm-lrg-acc-trigger]');
        if (!trigger) return;
        trigger.addEventListener('click', () => this.toggleAccordion(index));
      });
    }

    toggleAccordion(index) {
      const target = this.accItems[index];
      if (!target) return;
      const willOpen = !target.classList.contains('is-open');
      const trigger = target.querySelector('[data-bm-lrg-acc-trigger]');

      // Closing a taller item above would yank this question up out of the
      // viewport. Pin the clicked trigger's screen position so the panel only
      // grows downward under it (mobile).
      const anchorTop = trigger ? trigger.getBoundingClientRect().top : 0;

      this.accItems.forEach((item, i) => {
        const open = i === index && willOpen;
        const itemTrigger = item.querySelector('[data-bm-lrg-acc-trigger]');
        const panel = item.querySelector('[data-bm-lrg-acc-panel]');

        item.classList.toggle('is-open', open);
        if (itemTrigger) itemTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (panel) panel.hidden = !open;
      });

      if (trigger) {
        const settle = () => {
          const delta = trigger.getBoundingClientRect().top - anchorTop;
          if (Math.abs(delta) > 1) {
            window.scrollBy(0, delta);
          }
        };
        // Two frames: unhide + layout, then optional viewer remount height.
        window.requestAnimationFrame(() => {
          settle();
          window.requestAnimationFrame(settle);
        });
      }

      // Let the 3D viewer re-mount on the newly opened accordion host (same event
      // the desktop tabs fire).
      this.dispatchEvent(
        new CustomEvent('bm-lrg-panel-change', {
          bubbles: false,
          detail: { index, accordion: true },
        })
      );
    }

    onTriggerKeydown(event, index) {
      const { key } = event;
      if (key === 'Enter' || key === ' ') {
        event.preventDefault();
        this.goTo(index);
        return;
      }

      if (key === 'ArrowDown' || key === 'ArrowRight') {
        event.preventDefault();
        this.goTo((index + 1) % this.navItems.length);
        return;
      }

      if (key === 'ArrowUp' || key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo((index - 1 + this.navItems.length) % this.navItems.length);
      }
    }

    goTo(index) {
      const count = this.navItems.length;
      if (count === 0) return;

      const nextIndex = ((index % count) + count) % count;
      this.activeIndex = nextIndex;

      this.navItems.forEach((item, i) => {
        const isActive = i === nextIndex;
        const trigger = item.querySelector('[data-bm-lrg-nav-trigger]');

        item.classList.toggle('is-active', isActive);
        if (trigger) {
          trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
          trigger.setAttribute('tabindex', isActive ? '0' : '-1');
        }
      });

      this.panels.forEach((panel, i) => {
        const isActive = i === nextIndex;
        panel.classList.toggle('is-active', isActive);
        panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      const activeItem = this.navItems[nextIndex];
      if (activeItem) {
        activeItem.scrollIntoView({
          block: 'nearest',
          behavior: this.reducedMotion ? 'auto' : 'smooth',
        });
      }

      this.dispatchEvent(
        new CustomEvent('bm-lrg-panel-change', {
          bubbles: false,
          detail: { index: nextIndex },
        })
      );
    }
  }

  customElements.define('bm-landing-ring-guide', BmLandingRingGuide);
}
